// src/services/foodMenuParsingService.js
/**
 * Food Menu Parsing Service
 * Handles parsing and analysis of restaurant food menus for automatic pairing suggestions
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase-config";
import aiService from "./aiService.js";

export class FoodMenuParsingService {
  constructor() {
    this.foodMenuCollection = "food_menus";
    this.menuAnalysisCollection = "menu_analysis";
    this.pairingSuggestionsCollection = "pairing_suggestions";
  }

  /**
   * Parse and analyze a food menu from text or structured data
   * @param {string} restaurantId - Restaurant ID
   * @param {string|object} menuData - Menu text or structured data
   * @param {object} options - Parsing options
   */
  async parseAndAnalyzeFoodMenu(restaurantId, menuData, options = {}) {
    try {
      console.log(
        `🍽️ Starting food menu analysis for restaurant: ${restaurantId}`
      );

      const {
        menuType = "full", // 'full', 'appetizers', 'entrees', 'desserts'
        generatePairings = true,
        analysisLevel = "detailed", // 'basic', 'detailed', 'comprehensive'
      } = options;

      // Parse the menu structure
      const parsedMenu = await this.parseMenuStructure(menuData);

      // Analyze each dish for pairing characteristics
      const analyzedDishes = await this.analyzeDishesForPairings(
        parsedMenu.dishes
      );

      // Store the analyzed menu
      const menuAnalysis = {
        restaurantId,
        menuType,
        parsedAt: serverTimestamp(),
        totalDishes: analyzedDishes.length,
        dishes: analyzedDishes,
        menuStructure: parsedMenu.structure,
        analysisLevel,
        pairingCapabilities: this.calculatePairingCapabilities(analyzedDishes),
      };

      // Save to Firestore
      const menuAnalysisRef = doc(
        db,
        this.menuAnalysisCollection,
        `${restaurantId}_${menuType}`
      );
      await setDoc(menuAnalysisRef, menuAnalysis);

      // Generate automatic pairings if requested
      if (generatePairings) {
        await this.generateAutomaticPairings(restaurantId, analyzedDishes);
      }

      console.log(
        `✅ Food menu analysis completed: ${analyzedDishes.length} dishes analyzed`
      );
      return menuAnalysis;
    } catch (error) {
      console.error("Error parsing food menu:", error);
      throw error;
    }
  }

  /**
   * Parse menu structure from text or data
   * @param {string|object} menuData - Raw menu data
   */
  async parseMenuStructure(menuData) {
    const prompt = `
    Analyze this restaurant menu and extract structured information about each dish.
    For each dish, identify:
    1. Name
    2. Description
    3. Price (if available)
    4. Category (appetizer, soup, salad, entree, dessert, etc.)
    5. Primary ingredients
    6. Cooking method
    7. Flavor profile (spicy, sweet, savory, umami, etc.)
    8. Protein type (if any)
    9. Cuisine style
    10. Dietary restrictions (vegetarian, vegan, gluten-free, etc.)

    Menu data:
    ${typeof menuData === "string" ? menuData : JSON.stringify(menuData)}

    Return as JSON with structure:
    {
      "structure": {
        "categories": ["appetizers", "entrees", ...],
        "totalItems": number
      },
      "dishes": [
        {
          "name": "Dish Name",
          "description": "Description",
          "price": number,
          "category": "category",
          "ingredients": ["ingredient1", "ingredient2"],
          "cookingMethod": "grilled|braised|fried|raw|etc",
          "flavorProfile": ["savory", "spicy", "etc"],
          "proteinType": "beef|chicken|fish|vegetarian|etc",
          "cuisineStyle": "italian|french|american|etc",
          "dietaryRestrictions": ["vegetarian", "gluten-free", "etc"],
          "pairingCharacteristics": {
            "weight": "light|medium|heavy",
            "intensity": "delicate|moderate|bold",
            "primaryFlavors": ["flavor1", "flavor2"],
            "textureProfile": "creamy|crispy|tender|etc",
            "temperature": "hot|cold|room-temp",
            "spiceLevel": "none|mild|medium|hot"
          }
        }
      ]
    }
    `;

    const response = await aiService.generateContent(prompt);
    return JSON.parse(response);
  }

  /**
   * Analyze dishes for pairing characteristics
   * @param {Array} dishes - Array of dish objects
   */
  async analyzeDishesForPairings(dishes) {
    const analyzedDishes = [];

    for (const dish of dishes) {
      try {
        const pairingAnalysis = await this.analyzeDishForPairings(dish);
        analyzedDishes.push({
          ...dish,
          pairingAnalysis,
          analyzedAt: new Date().toISOString(),
        });
      } catch (error) {
        console.error(`Error analyzing dish ${dish.name}:`, error);
        analyzedDishes.push({
          ...dish,
          pairingAnalysis: null,
          analysisError: error.message,
        });
      }
    }

    return analyzedDishes;
  }

  /**
   * Deep analyze a single dish for pairing characteristics
   * @param {object} dish - Dish object
   */
  async analyzeDishForPairings(dish) {
    const prompt = `
    Analyze this dish for beverage pairing characteristics:

    Dish: ${dish.name}
    Description: ${dish.description}
    Ingredients: ${dish.ingredients?.join(", ")}
    Cooking Method: ${dish.cookingMethod}
    Flavor Profile: ${dish.flavorProfile?.join(", ")}
    Protein: ${dish.proteinType}
    Cuisine: ${dish.cuisineStyle}

    Provide detailed pairing analysis in JSON format:
    {
      "wineCompatibility": {
        "redWines": {
          "compatibility": "high|medium|low",
          "specificStyles": ["cabernet", "pinot noir", "etc"],
          "reasoning": "explanation"
        },
        "whiteWines": {
          "compatibility": "high|medium|low",
          "specificStyles": ["chardonnay", "sauvignon blanc", "etc"],
          "reasoning": "explanation"
        },
        "rosePairings": {
          "compatibility": "high|medium|low",
          "reasoning": "explanation"
        },
        "sparklingWines": {
          "compatibility": "high|medium|low",
          "reasoning": "explanation"
        }
      },
      "beerCompatibility": {
        "ales": {
          "compatibility": "high|medium|low",
          "specificStyles": ["IPA", "pale ale", "etc"],
          "reasoning": "explanation"
        },
        "lagers": {
          "compatibility": "high|medium|low",
          "specificStyles": ["pilsner", "märzen", "etc"],
          "reasoning": "explanation"
        },
        "stouts": {
          "compatibility": "high|medium|low",
          "reasoning": "explanation"
        }
      },
      "spiritCompatibility": {
        "whiskey": {
          "compatibility": "high|medium|low",
          "specificTypes": ["bourbon", "rye", "scotch"],
          "reasoning": "explanation"
        },
        "gin": {
          "compatibility": "high|medium|low",
          "reasoning": "explanation"
        },
        "vodka": {
          "compatibility": "high|medium|low",
          "reasoning": "explanation"
        },
        "rum": {
          "compatibility": "high|medium|low",
          "reasoning": "explanation"
        }
      },
      "cocktailSuggestions": [
        {
          "name": "Cocktail Name",
          "baseSpirit": "spirit",
          "compatibility": "high|medium|low",
          "reasoning": "why this cocktail pairs well"
        }
      ],
      "overallPairingStrategy": {
        "primaryPairingApproach": "complement|contrast|bridge",
        "keyConsiderations": ["consideration1", "consideration2"],
        "recommendedCategories": ["wine", "beer", "spirits", "cocktails"],
        "avoidCategories": ["category to avoid"],
        "confidenceScore": 0.85
      }
    }
    `;

    const response = await aiService.generateContent(prompt);
    return JSON.parse(response);
  }

  /**
   * Generate automatic pairing suggestions for analyzed dishes
   * @param {string} restaurantId - Restaurant ID
   * @param {Array} analyzedDishes - Array of analyzed dish objects
   */
  async generateAutomaticPairings(restaurantId, analyzedDishes) {
    try {
      console.log(
        `🍷 Generating automatic pairings for ${analyzedDishes.length} dishes`
      );

      // Get restaurant's beverage inventory
      const beverageInventory = await this.getRestaurantBeverageInventory(
        restaurantId
      );

      const allPairings = [];

      for (const dish of analyzedDishes) {
        if (!dish.pairingAnalysis) continue;

        const dishPairings = await this.generateDishPairings(
          dish,
          beverageInventory
        );
        allPairings.push(...dishPairings);
      }

      // Save all pairings to Firestore
      const pairingsRef = doc(
        db,
        this.pairingSuggestionsCollection,
        `${restaurantId}_auto`
      );
      await setDoc(pairingsRef, {
        restaurantId,
        generatedAt: serverTimestamp(),
        totalPairings: allPairings.length,
        pairings: allPairings,
        version: "1.0",
      });

      console.log(`✅ Generated ${allPairings.length} automatic pairings`);
      return allPairings;
    } catch (error) {
      console.error("Error generating automatic pairings:", error);
      throw error;
    }
  }

  /**
   * Generate specific pairings for a dish with available beverages
   * @param {object} dish - Analyzed dish object
   * @param {Array} beverageInventory - Available beverages
   */
  async generateDishPairings(dish, beverageInventory) {
    const pairings = [];

    for (const beverage of beverageInventory) {
      try {
        const pairingScore = await this.calculatePairingScore(dish, beverage);

        if (pairingScore.score >= 0.6) {
          // Only include good pairings
          pairings.push({
            dishId: dish.id || dish.name,
            dishName: dish.name,
            beverageId: beverage.id,
            beverageName: beverage.name,
            beverageCategory: beverage.category,
            pairingScore: pairingScore.score,
            confidence: pairingScore.confidence,
            reasoning: pairingScore.reasoning,
            pairingType: pairingScore.type, // 'complement', 'contrast', 'bridge'
            createdAt: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error(
          `Error calculating pairing for ${dish.name} + ${beverage.name}:`,
          error
        );
      }
    }

    return pairings.sort((a, b) => b.pairingScore - a.pairingScore);
  }

  /**
   * Calculate pairing confidence score between a dish and beverage
   * @param {object} dish - Dish with pairing analysis
   * @param {object} beverage - Beverage object
   */
  async calculatePairingScore(dish, beverage) {
    const prompt = `
    Calculate a pairing compatibility score between this dish and beverage:

    DISH:
    Name: ${dish.name}
    Description: ${dish.description}
    Flavor Profile: ${dish.flavorProfile?.join(", ")}
    Cooking Method: ${dish.cookingMethod}
    Protein: ${dish.proteinType}
    Weight: ${dish.pairingCharacteristics?.weight}
    Intensity: ${dish.pairingCharacteristics?.intensity}

    BEVERAGE:
    Name: ${beverage.name}
    Category: ${beverage.category}
    Description: ${beverage.description}
    ${
      beverage.aiEnhanced
        ? `
    Tasting Notes: ${JSON.stringify(beverage.aiEnhanced?.tastingNotes)}
    Producer: ${beverage.aiEnhanced?.producer?.name}
    Style: ${beverage.aiEnhanced?.style?.name}
    `
        : ""
    }

    Return JSON with:
    {
      "score": 0.85, // 0-1 compatibility score
      "confidence": 0.92, // 0-1 confidence in this assessment
      "type": "complement", // "complement", "contrast", or "bridge"
      "reasoning": "Detailed explanation of why they pair well or don't",
      "specificReasons": [
        "reason 1",
        "reason 2"
      ],
      "warnings": ["any potential issues"],
      "improvementSuggestions": ["how to enhance the pairing"]
    }
    `;

    const response = await aiService.generateContent(prompt);
    return JSON.parse(response);
  }

  /**
   * Get restaurant's beverage inventory
   * @param {string} restaurantId - Restaurant ID
   */
  async getRestaurantBeverageInventory(restaurantId) {
    try {
      const beveragesQuery = query(
        collection(db, "menu_items"),
        where("restaurantId", "==", restaurantId),
        where("type", "!=", "header")
      );

      const snapshot = await getDocs(beveragesQuery);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting beverage inventory:", error);
      return [];
    }
  }

  /**
   * Calculate overall pairing capabilities for a menu
   * @param {Array} analyzedDishes - Array of analyzed dishes
   */
  calculatePairingCapabilities(analyzedDishes) {
    const capabilities = {
      totalDishes: analyzedDishes.length,
      wineCompatibleDishes: 0,
      beerCompatibleDishes: 0,
      spiritCompatibleDishes: 0,
      cocktailCompatibleDishes: 0,
      averageComplexity: 0,
      cuisineStyles: new Set(),
      flavorProfiles: new Set(),
    };

    analyzedDishes.forEach((dish) => {
      if (dish.cuisineStyle) {
        capabilities.cuisineStyles.add(dish.cuisineStyle);
      }

      if (dish.flavorProfile) {
        dish.flavorProfile.forEach((flavor) =>
          capabilities.flavorProfiles.add(flavor)
        );
      }

      // Count pairing compatibilities (assuming high/medium compatibility)
      const analysis = dish.pairingAnalysis;
      if (analysis) {
        if (
          analysis.wineCompatibility &&
          Object.values(analysis.wineCompatibility).some(
            (wine) =>
              wine.compatibility === "high" || wine.compatibility === "medium"
          )
        ) {
          capabilities.wineCompatibleDishes++;
        }

        if (
          analysis.beerCompatibility &&
          Object.values(analysis.beerCompatibility).some(
            (beer) =>
              beer.compatibility === "high" || beer.compatibility === "medium"
          )
        ) {
          capabilities.beerCompatibleDishes++;
        }

        if (
          analysis.spiritCompatibility &&
          Object.values(analysis.spiritCompatibility).some(
            (spirit) =>
              spirit.compatibility === "high" ||
              spirit.compatibility === "medium"
          )
        ) {
          capabilities.spiritCompatibleDishes++;
        }

        if (
          analysis.cocktailSuggestions &&
          analysis.cocktailSuggestions.length > 0
        ) {
          capabilities.cocktailCompatibleDishes++;
        }
      }
    });

    // Convert Sets to Arrays for JSON serialization
    capabilities.cuisineStyles = Array.from(capabilities.cuisineStyles);
    capabilities.flavorProfiles = Array.from(capabilities.flavorProfiles);

    return capabilities;
  }

  /**
   * Get pairing suggestions for a specific dish
   * @param {string} restaurantId - Restaurant ID
   * @param {string} dishName - Dish name
   */
  async getPairingSuggestionsForDish(restaurantId, dishName) {
    try {
      const pairingsRef = doc(
        db,
        this.pairingSuggestionsCollection,
        `${restaurantId}_auto`
      );
      const pairingsSnap = await getDoc(pairingsRef);

      if (!pairingsSnap.exists()) {
        return [];
      }

      const pairingsData = pairingsSnap.data();
      return pairingsData.pairings.filter((pairing) =>
        pairing.dishName.toLowerCase().includes(dishName.toLowerCase())
      );
    } catch (error) {
      console.error("Error getting pairing suggestions:", error);
      return [];
    }
  }

  /**
   * Update pairing scores based on customer feedback
   * @param {string} restaurantId - Restaurant ID
   * @param {string} pairingId - Pairing ID
   * @param {number} customerRating - Customer rating (1-5)
   * @param {string} feedback - Optional feedback
   */
  async updatePairingFeedback(
    restaurantId,
    pairingId,
    customerRating,
    feedback = ""
  ) {
    try {
      // This would implement machine learning feedback loop
      // For now, we'll store the feedback for future analysis

      const feedbackRef = doc(
        db,
        "pairing_feedback",
        `${restaurantId}_${pairingId}_${Date.now()}`
      );
      await setDoc(feedbackRef, {
        restaurantId,
        pairingId,
        customerRating,
        feedback,
        submittedAt: serverTimestamp(),
      });

      console.log(`📝 Pairing feedback recorded: ${customerRating}/5`);
    } catch (error) {
      console.error("Error updating pairing feedback:", error);
      throw error;
    }
  }
}

export const foodMenuParsingService = new FoodMenuParsingService();
